import React from "react";
import ReactPaginate from "react-paginate";
import PropTypes from "prop-types";
import styled from "styled-components";
import SelectDropdown from "fields/SelectDropdown";
import Icon from "components/icons";

const MainContainer = styled.div`
  max-width: 100%;
  padding: 10px 0;
  display: grid;
  align-items: center;
  row-gap: 1.5rem;
  justify-items: center;
  grid-template-areas:
    "pagination"
    "page-size";

  ${(props) => props.theme.mq.sm`
    display: flex;
    justify-content: space-between;
  `}
`;

const LeftContainer = styled.div`
  grid-area: page-size;
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const PaginationContainer = styled.div`
  grid-area: pagination;

  ul,
  .ScrollContainter {
    list-style-type: none;
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    :hover {
      cursor: pointer;
    }
  }
  li {
    margin: 0 2px 0 2px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  a {
    width: 100%;
    height: 100%;
    align-items: center;
    display: flex;
    justify-content: center;
    svg {
      display: flex;
      align-items: center;
      margin: 6px 0 0 0 !important;
    }
  }

  .previous,
  .next {
    :hover {
      opacity: 0.5;
    }
  }

  .next {
    svg {
      margin-left: 0.7rem !important;
    }
  }

  .pages {
    border: none;
    width: 2em;
    height: 2em;
    border-radius: 17px;
    line-height: 2em;
    text-align: center;
    max-height: 2em;
    max-width: 2em;
    min-height: 0.5em;
    min-width: 0.5em;
    margin: 0 5px;
    background-color: ${(props) => props.theme.themeColors.lightGreen4};
    :hover {
      background-color: ${(props) => props.theme.themeColors.lightGreen2};
      border-radius: 17px;
    }
  }
  .pages.current {
    margin: 0 0 0 3px;
    height: 2rem;
    width: 2rem;
    background-color: ${(props) => props.theme.themeColors.primary};
    color: white;
    :hover {
      opacity: 0.5;
    }
  }
`;

const StyledDropdown = styled(SelectDropdown)`
  width: 3rem;
  margin: 0 8px 0 17px;
  font-size: ${({ theme }) => theme.fontSize(0)};
  .gfx-select__option {
    color: ${(props) => props.theme.themeColors.dark};
    &.gfx-select__option--is-focused {
      background-color: white;
      color: ${(props) => props.theme.themeColors.dark};
      opacity: 0.5;
    }
    &.gfx-select__option--is-selected {
      background-color: white;
      color: ${(props) => props.theme.themeColors.primary};
      opacity: 1;
    }
  }
  ${(props) => props.theme.mq.lg`
    min-width: 50px;
  `}
`;

const SubHeading = styled.div`
  font-size: ${({ theme }) => theme.fontSize(0)};
  color: ${(props) => props.theme.secondary};
  margin: 0 5px 0 5px;
  white-space: nowrap;
  font-weight: 400;
`;

const Break = styled.div`
  display: flex;
  align-items: flex-end;
  text-align: center;
  justify-content: center;
  border: none;
  border-radius: 17px;
  line-height: 32px;
  font-weight: 600;
  text-align: center;
  width: 32px;
  height: 32px;
  max-height: 70%;
  margin: 0 4px 0 4px;
  background-color: ${(props) => props.theme.themeColors.lightGreen1};
`;

const DesktopPaginationContainer = styled(PaginationContainer)`
  display: none;

  ${({ theme }) => theme.mq.sm`
    display: block;
  `}
`;

const MobilePaginationContainer = styled(PaginationContainer)`
  display: block;

  ${({ theme }) => theme.mq.sm`
    display: none;
  `}
`;

export default function Pagination({
  totalPages,
  currentPage,
  pageSize,
  selectPage,
  pageRangeDisplayed,
  setPageSize,
  marginPagesDisplayed,
  handlePageSelection,
  pages,
}) {
  if (!totalPages || totalPages < 2) {
    return null;
  }

  const props = {
    previousLabel: <Icon name="left-arrow-20" color="#27272F" />,
    nextLabel: <Icon name="right-arrow-20" color="#27272F" />,
    pageCount: totalPages,
    marginPagesDisplayed,
    breakLabel: <Break>...</Break>,
    forcePage: parseInt(currentPage, 10) - 1,
    pageRangeDisplayed,
    onPageChange: ({ selected }) => {
      const nextPage = selected + 1;
      selectPage(nextPage);
      if (handlePageSelection) {
        handlePageSelection(nextPage);
      }
    },
    containerClassName: "ScrollContainer",
    pageClassName: "pages",
    activeClassName: "current",
  };

  const drop = {
    input: {
      onChange: (selected) => {
        setPageSize(selected);
      },
      value: pageSize,
    },
  };
  const options = pages || [
    { value: 4, label: "4" },
    { value: 6, label: "6" },
    { value: 8, label: "8" },
  ];

  return (
    <MainContainer>
      <LeftContainer>
        <SubHeading>
          {currentPage} of {totalPages}
        </SubHeading>
        <StyledDropdown options={options} {...drop} />
        <SubHeading>Results per page</SubHeading>
      </LeftContainer>
      <DesktopPaginationContainer>
        <ReactPaginate {...props} pageRangeDisplayed={5} />
      </DesktopPaginationContainer>

      <MobilePaginationContainer>
        <ReactPaginate {...props} pageRangeDisplayed={1} />
      </MobilePaginationContainer>
    </MainContainer>
  );
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  handlePageSelection: PropTypes.func,
  selectPage: PropTypes.func,
  pageRangeDisplayed: PropTypes.number,
  marginPagesDisplayed: PropTypes.number,
  setPageSize: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  pages: PropTypes.any,
};

Pagination.defaultProps = {
  marginPagesDisplayed: 1,
  pageRangeDisplayed: 5,
  handlePageSelection: undefined,
  selectPage: undefined,
  pages: "",
};
